import {
  ComponentRef,
  FC,
  FunctionComponent,
  Suspense,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import { lowerCase, uniqueId } from 'lodash';
import { FieldArray, useFormikContext } from 'formik';
import type { FieldArrayRenderProps } from 'formik';
import { cn, useAdmin } from 'utils/helpers';
import { BasicStepper, Button } from 'ui/components/common';
import { SIZE } from 'utils/constants';
import { Input } from 'ui/components';
import {
  Checkbox,
  Currency,
  DateField,
  MultiSelect,
  PhoneNumber,
  Radio,
  RadioGroup,
  Select,
  Switch,
  Textarea,
} from 'ui/components/form';
import { OPTIONS_PROPS } from 'ui/components/nav/Dropdown/Dropdown';
import {
  DATA_DATE_FORMAT,
  DATA_NUMBER_FORMAT,
  DATA_SOURCE,
  DATA_STRING_FORMAT,
  DATA_TYPE,
} from 'utils/types';
import { startCase } from 'lodash';
import { DragIcon, PlusIcon } from 'ui/components/icon';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import type { DropResult } from 'react-beautiful-dnd';
import { CheckboxGroup } from 'ui/components/form/Checkbox/Checkbox';
import { CREATE_FIELD_FORM } from 'ui/partials/Composer/ComposerFieldBody';
import { Tabs } from 'ui/components/nav';

import { useDataManagementContext } from '../containers/DataManagement';

type CreateFieldFormProps = {
  mode?: 'popover' | 'modal';
};

type Steps = 'basic-info' | 'field-type' | 'rules';

const CreateFieldFormPopover: FC<CreateFieldFormProps> = ({ mode }) => {
  const { handleSubmit, values, resetForm } =
    useFormikContext<CREATE_FIELD_FORM>();

  const { selectedFieldId, handleClose } = useDataManagementContext();
  const [step, setStep] = useState<Steps>('basic-info');
  const { isAdmin } = useAdmin();

  const handleCancel = useCallback(() => {
    handleClose?.(resetForm, () => setStep('basic-info'));
  }, [handleClose, resetForm]);

  const componentToRender = useMemo(() => {
    switch (step) {
      case 'basic-info':
        return <BasicInfo setStep={setStep} handleCancel={handleCancel} />;
      case 'field-type':
        return <FieldType setStep={setStep} handleCancel={handleCancel} />;
      default:
        return <Rules setStep={setStep} handleCancel={handleCancel} />;
    }
  }, [handleCancel, step]);

  const steps: { label: string; value: Steps }[] = [
    { label: 'Basic Info', value: 'basic-info' },
    { label: 'Field Type', value: 'field-type' },
    { label: 'Rules', value: 'rules' },
  ];
  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        'relative',
        'flex flex-col justify-between',
        mode === 'popover'
          ? isAdmin
            ? 'h-[calc(100vh-122px)]'
            : 'h-[calc(100vh-100px)]'
          : 'min-h-[630px]',
      )}
    >
      <div className="space-y-5 h-full px-[22px] py-4 ">
        <div className="space-y-[6px]">
          <div className="font-semibold leading-6 tracking-[0.64px]">
            {`${selectedFieldId ? 'Edit' : 'Create'} Field`}
          </div>
          {selectedFieldId ? (
            <Tabs
              type="secondary"
              size={SIZE.Small}
              selectedTab={step}
              items={steps}
              onChange={({ value }) => setStep(value as typeof step)}
            />
          ) : (
            <div className="flex justify-center items-center">
              <BasicStepper steps={steps} activeStepValue={step} />
            </div>
          )}
        </div>
        {step !== 'basic-info' && (
          <div
            className={cn(
              'flex items-center justify-center text-xl font-semibold',
              'h-10 border-[0.5px] rounded-[4px] border-separator-secondary',
            )}
          >
            {values.name}
          </div>
        )}
        {componentToRender}
        {selectedFieldId && (
          <div
            className={cn(
              'absolute',
              'bottom-0 left-0 w-full',
              'flex justify-end gap-3',
              'px-2.5 py-5 mt-5',
              'border-t-[0.5px] border-separator-primary',
              'flex-1',
            )}
          >
            <Button
              onClick={handleCancel}
              label="CANCEL"
              size="sm"
              intent="tertiary"
            />
            <Button type="submit" label="SAVE" size="sm" />
          </div>
        )}
      </div>
    </form>
  );
};

export default CreateFieldFormPopover;

type CommonProps = {
  setStep: React.Dispatch<
    React.SetStateAction<'basic-info' | 'field-type' | 'rules'>
  >;
  handleCancel: () => void;
};

const BasicInfo: FC<CommonProps> = ({ setStep, handleCancel }) => {
  const { setFieldValue, touched, handleChange, handleBlur, errors, values } =
    useFormikContext<CREATE_FIELD_FORM>();
  const { selectedFieldId } = useDataManagementContext();

  const sourceOptions: OPTIONS_PROPS[] = [
    { value: DATA_SOURCE.LEAD, label: 'LEADS' },
    { value: DATA_SOURCE.COMPANY, label: 'COMPANIES' },
    { value: DATA_SOURCE.DEAL, label: 'DEALS' },
    // Uncomment items below if we now support them.
    // { value: DATA_SOURCE.TASK, label: 'TASKS' },
  ];

  return (
    <div className="flex flex-col justify-between">
      <div className="space-y-5 flex-1">
        <Select
          disabled={values.isInUse}
          required
          name="source"
          placeholder="Select source"
          label="Source"
          options={sourceOptions}
          onChange={(val) => setFieldValue('source', val.value)}
          textType={errors.source && touched.source ? 'danger' : 'default'}
          subText={errors.source && touched.source ? errors.source : undefined}
          defaultValue={values.source}
        />
        <Input
          className="w-full"
          autoComplete="off"
          type="text"
          name="name"
          size={SIZE.Medium}
          textType={errors.name && touched.name ? 'danger' : 'default'}
          subText={errors.name && touched.name ? errors.name : ''}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.name}
          placeholder="Input name"
          label="Name"
          required
        />
        <Textarea
          className="w-full"
          name="description"
          size={SIZE.Medium}
          textType={
            errors.description && touched.description ? 'danger' : 'default'
          }
          onChange={(e) => setFieldValue('description', e)}
          onBlur={handleBlur}
          value={values.description}
          placeholder="Input description"
          subText={
            errors.description && touched.description ? errors.description : ''
          }
          label="Description"
        />
      </div>
      {!selectedFieldId && (
        <div
          className={cn(
            'absolute',
            'bottom-0 left-0 w-full',
            'flex justify-end gap-3',
            'px-2.5 py-5 mt-5',
            'border-t-[0.5px] border-separator-primary',
            'flex-1',
          )}
        >
          <Button
            onClick={handleCancel}
            label="CANCEL"
            size="sm"
            intent="tertiary"
          />
          <Button
            onClick={() => setStep('field-type')}
            type="button"
            label="NEXT"
            size="sm"
            disabled={!values.name || !values.source}
          />
        </div>
      )}
    </div>
  );
};
const FieldType: FC<CommonProps> = ({ setStep, handleCancel: hideModal }) => {
  const { setFieldValue, touched, errors, values } =
    useFormikContext<CREATE_FIELD_FORM>();
  const { selectedFieldId } = useDataManagementContext();

  const typeOptions: OPTIONS_PROPS[] = [
    { value: DATA_TYPE.STRING, label: 'Text' },
    { value: DATA_TYPE.MOBILENUMBER, label: 'Phone Number' },
    { value: DATA_TYPE.NUMBER, label: 'Number' },
    { value: DATA_TYPE.DATE, label: 'Date Picker' },
    { value: DATA_TYPE.SELECT, label: 'Select Dropdown' },
    { value: DATA_TYPE.MULTISELECT, label: 'Multi-select Dropdown' },
    { value: DATA_TYPE.CHECKBOX, label: 'Checkbox' },
    { value: DATA_TYPE.RADIOBOX, label: 'Radio' },
    { value: DATA_TYPE.BOOLEAN, label: 'Toggle' },
  ];

  const arrayTypes = [
    DATA_TYPE.MULTISELECT,
    DATA_TYPE.SELECT,
    DATA_TYPE.RADIOBOX,
    DATA_TYPE.CHECKBOX,
  ];

  const numberFormatOptions: OPTIONS_PROPS[] = [
    {
      label: startCase(lowerCase(DATA_NUMBER_FORMAT.FORMATTED)),
      value: DATA_NUMBER_FORMAT.FORMATTED,
    },
    {
      label: startCase(lowerCase(DATA_NUMBER_FORMAT.UNFORMATTED)),
      value: DATA_NUMBER_FORMAT.UNFORMATTED,
    },
    {
      label: startCase(lowerCase(DATA_NUMBER_FORMAT.CURRENCY)),
      value: DATA_NUMBER_FORMAT.CURRENCY,
    },
    {
      label: startCase(lowerCase(DATA_NUMBER_FORMAT.PERCENTAGE)),
      value: DATA_NUMBER_FORMAT.PERCENTAGE,
    },
  ];

  const dateFormatOptions: OPTIONS_PROPS[] = [
    {
      label: startCase(lowerCase(DATA_DATE_FORMAT.DATE)),
      value: DATA_DATE_FORMAT.DATE,
    },
    {
      label: startCase(lowerCase(DATA_DATE_FORMAT.DATETIME)),
      value: DATA_DATE_FORMAT.DATETIME,
    },
  ];

  const stringFormatOptions: OPTIONS_PROPS[] = [
    { value: DATA_STRING_FORMAT.STRING, label: 'Single-line Text' },
    { value: DATA_STRING_FORMAT.LONGSTRING, label: 'Multi-line Text' },
  ];

  const hasOptionErrors = useMemo(() => {
    const hasLabelErrors = (
      errors.options as unknown as { label: string; value: string }[]
    )?.some((val) => val.label !== '');
    const hasValueErrors = (
      errors.options as unknown as { label: string; value: string }[]
    )?.some((val) => val.value !== '');
    return hasLabelErrors || hasValueErrors;
  }, [errors.options]);

  return (
    <div className="space-y-5">
      <Select
        required
        name="type"
        placeholder="Select type"
        label="Type"
        disabled={values.isInUse}
        options={typeOptions}
        onChange={(val) => setFieldValue('type', val.value)}
        textType={errors.type && touched.type ? 'danger' : 'default'}
        subText={errors.type && touched.type ? errors.type : undefined}
        defaultValue={values.type}
      />

      {values.type && arrayTypes.includes(values.type) && (
        <Suspense fallback={null}>
          <FieldArray
            name="options"
            component={
              OptionsBuilder as FunctionComponent<void | FieldArrayRenderProps>
            }
          />
        </Suspense>
      )}

      {values.type === DATA_TYPE.NUMBER && (
        <>
          <Select
            disabled={values.isInUse}
            onChange={(val: OPTIONS_PROPS) =>
              setFieldValue('numberFormat', val.value)
            }
            label="Number format"
            defaultValue={values.numberFormat}
            options={numberFormatOptions}
          />
        </>
      )}

      {values.type === DATA_TYPE.DATE && (
        <>
          <Select
            disabled={values.isInUse}
            onChange={(val: OPTIONS_PROPS) =>
              setFieldValue('dateFormat', val.value)
            }
            label="Date format"
            defaultValue={values.dateFormat}
            options={dateFormatOptions}
          />
        </>
      )}

      {values.type === DATA_TYPE.STRING && (
        <>
          <Select
            disabled={values.isInUse}
            onChange={(val: OPTIONS_PROPS) =>
              setFieldValue('stringFormat', val.value)
            }
            label="String format"
            defaultValue={values.stringFormat ?? ''}
            options={stringFormatOptions}
          />
        </>
      )}

      {values.type && (
        <div className="p-5 space-y-2 bg-fill-primary rounded-md ">
          <div className="text-xs">Preview</div>
          <Preview type={values.type as DATA_TYPE} />
        </div>
      )}
      {!selectedFieldId && (
        <div
          className={cn(
            'absolute',
            'bottom-0 left-0 w-full',
            'flex justify-between gap-3',
            'px-2.5 py-5 mt-5',
            'border-t-[0.5px] border-separator-primary',
          )}
        >
          <Button
            onClick={() => setStep('basic-info')}
            label="BACK"
            size="sm"
            intent="text"
          />
          <div className="flex gap-3 items-center">
            <Button
              onClick={hideModal}
              label="CANCEL"
              size="sm"
              intent="tertiary"
            />
            <Button
              disabled={!values.type || hasOptionErrors}
              onClick={() => setStep('rules')}
              type="submit"
              label="NEXT"
              size="sm"
            />
          </div>
        </div>
      )}
    </div>
  );
};

const StringNumberRules = () => {
  const { setFieldValue, values } = useFormikContext<CREATE_FIELD_FORM>();

  const isString = values.type === DATA_TYPE.STRING;

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <Checkbox
          label={`Set minimum ${isString ? 'character' : 'value'} limit`}
          checked={!!values.rules.setMinLimit}
          onChange={(e) => setFieldValue('rules.setMinLimit', e.target.checked)}
        />
        {values.rules.setMinLimit && (
          <div className="pl-5">
            <Input
              onChange={(e) => setFieldValue('rules.min', e.target.value)}
              type="number"
              value={values.rules.min ?? 1}
              min={1}
            />
          </div>
        )}
      </div>
      <div className="space-y-2">
        <Checkbox
          label={`Set maximum ${isString ? 'character' : 'value'} limit`}
          checked={!!values.rules.setMaxLimit}
          onChange={(e) => setFieldValue('rules.setMaxLimit', e.target.checked)}
        />
        {values.rules.setMaxLimit && (
          <div className="pl-5">
            <Input
              onChange={(e) => setFieldValue('rules.max', e.target.value)}
              type="number"
              value={values.rules.max ?? 100}
              min={1}
              max={10000}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const Rules: FC<CommonProps> = ({ setStep, handleCancel: handleCancel }) => {
  const { setFieldValue, values, initialValues } =
    useFormikContext<CREATE_FIELD_FORM>();
  const { selectedFieldId } = useDataManagementContext();

  const hasStringNumberRules = useMemo(() => {
    if (values.type === DATA_TYPE.STRING || values.type === DATA_TYPE.NUMBER)
      return true;
    return false;
  }, [values.type]);

  return (
    <div className="space-y-3">
      <div className="text-base font-semibold leading-6 tracking-wider">
        Select fields rules
      </div>
      <div className="space-y-5">
        <div className="space-y-[6px]">
          <div className="font-semibold text-sm leading-[22px] tracking-wider">
            Validation Rules
          </div>
          <div className="text-sm leading-[22px] tracking-wider">
            Specify what values are allowed for this property. These rules apply
            when creating, editing, and importing records with the CRM.
          </div>
        </div>
      </div>
      {hasStringNumberRules && <StringNumberRules />}
      {!selectedFieldId && (
        <div
          className={cn(
            'absolute',
            'bottom-0 left-0 w-full',
            'flex justify-between gap-3',
            'px-2.5 py-5 mt-5',
            'border-t-[0.5px] border-separator-primary',
          )}
        >
          <Button
            onClick={() => {
              setFieldValue('rules', initialValues.rules);
              setStep('field-type');
            }}
            label="BACK"
            size="sm"
            intent="text"
          />
          <div className="flex gap-3 items-center">
            <Button
              onClick={handleCancel}
              label="CANCEL"
              size="sm"
              intent="tertiary"
            />
            <Button type="submit" label="SAVE" size="sm" />
          </div>
        </div>
      )}
    </div>
  );
};

type PreviewProps = { type: DATA_TYPE };

const Preview: FC<PreviewProps> = ({ type }) => {
  switch (type) {
    case DATA_TYPE.STRING:
      return <PreviewString />;
    case DATA_TYPE.MOBILENUMBER:
      return <PreviewPhoneNumber />;
    case DATA_TYPE.NUMBER:
      return <PreviewNumber />;
    case DATA_TYPE.CHECKBOX:
      return <PreviewCheckbox />;
    case DATA_TYPE.MULTISELECT:
      return <PreviewMultiSelect />;
    case DATA_TYPE.SELECT:
      return <PreviewSingleSelect />;
    case DATA_TYPE.DATE:
      return <PreviewDateTime />;
    case DATA_TYPE.RADIOBOX:
      return <PreviewRadioBox />;
    case DATA_TYPE.BOOLEAN:
      return <PreviewToggle />;
  }
};

const PreviewPhoneNumber = () => {
  const { values } = useFormikContext<CREATE_FIELD_FORM>();

  const [value, setValue] = useState('');

  return (
    <PhoneNumber
      className="w-full"
      name="contactNumber"
      label={values.name}
      value={value}
      onChange={(e) => setValue(e)}
    />
  );
};

const PreviewMultiSelect = () => {
  const { values } = useFormikContext<CREATE_FIELD_FORM>();

  return (
    <MultiSelect
      label={values.name}
      onChange={() => {}}
      options={values.options ?? []}
    />
  );
};

const PreviewSingleSelect = () => {
  const { values } = useFormikContext<CREATE_FIELD_FORM>();

  return (
    <Select
      label={values.name}
      onChange={() => {}}
      options={values.options ?? []}
    />
  );
};

const PreviewToggle = () => {
  const { values } = useFormikContext<CREATE_FIELD_FORM>();
  const label = values.name;

  const [value, setValue] = useState(false);

  return (
    <Switch
      checked={value}
      label={label}
      onClick={() => setValue((prev) => !prev)}
    />
  );
};

const PreviewRadioBox = () => {
  const { values } = useFormikContext<CREATE_FIELD_FORM>();
  const label = values.name;
  const [value, setValue] = useState<any | null>();

  return (
    <RadioGroup
      groupLabel={label}
      groupClassName="flex gap-3 flex-wrap required"
    >
      {values.options?.map((d) => (
        <Radio
          key={d.id}
          checked={d.value === value}
          label={d.label}
          value={d.value}
          defaultChecked={d.value === value}
          onClick={() => setValue(d.value)}
        />
      ))}
    </RadioGroup>
  );
};

const PreviewDateTime = () => {
  const { values } = useFormikContext<CREATE_FIELD_FORM>();
  const label = values.name;
  const [value, setValue] = useState<Date | null>(null);

  const dateFormat = values.dateFormat;

  if (dateFormat === DATA_DATE_FORMAT.DATE)
    return (
      <DateField
        onChange={(_e, value) => setValue(value as Date)}
        selected={value}
        name="preview"
        label={label}
      />
    );

  return (
    <DateField
      label={label}
      showTimeSelect
      selected={value}
      name="date"
      dateFormat={'MM/dd/yyyy hh:mm a'}
      onChange={(_e, value) => setValue(value as Date)}
    />
  );
};

const PreviewCheckbox = () => {
  const { values } = useFormikContext<CREATE_FIELD_FORM>();

  const label = values.name;
  const [selected, setSelected] = useState<{ label: string; value: string }[]>(
    [],
  );

  return (
    <CheckboxGroup
      groupLabel={label}
      groupClassName="flex gap-3 flex-wrap required"
    >
      {values.options?.map((opt) => {
        const isSelected = selected.find((d) => d.value === opt.value);

        return (
          <Checkbox
            checked={!!isSelected}
            label={opt.label}
            value={opt.value}
            onChange={() => {
              if (isSelected) {
                setSelected((prev) =>
                  prev.filter((d) => d.value !== opt.value),
                );
              } else {
                setSelected((prev) => [...prev, opt]);
              }
            }}
          />
        );
      })}
    </CheckboxGroup>
  );
};

const PreviewString = () => {
  const { values } = useFormikContext<CREATE_FIELD_FORM>();

  const label = values.name;
  const [value, setValue] = useState('');

  if (values.stringFormat === DATA_STRING_FORMAT.STRING) {
    return (
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        label={label}
      />
    );
  } else {
    return (
      <Textarea value={value} label={label} onChange={(e) => setValue(e)} />
    );
  }
};

const PreviewNumber = () => {
  const { values } = useFormikContext<CREATE_FIELD_FORM>();

  const label = values.name;
  const numberFormat = values.numberFormat;

  const [value, setValue] = useState('');

  switch (numberFormat) {
    case DATA_NUMBER_FORMAT.FORMATTED:
      return (
        <Currency
          allowNegativeValue
          value={value}
          decimalsLimit={10}
          hidePrefix
          label={label}
          onValueChange={(value) => setValue(value ?? '')}
        />
      );
    case DATA_NUMBER_FORMAT.UNFORMATTED:
      return (
        <Input
          label={label}
          value={value}
          type="number"
          onChange={(e) => setValue(e.target.value)}
        />
      );
    case DATA_NUMBER_FORMAT.CURRENCY:
      return (
        <Currency
          allowNegativeValue
          label={label}
          value={value}
          onValueChange={(value) => setValue(value ?? '')}
        />
      );
    case DATA_NUMBER_FORMAT.PERCENTAGE:
      return (
        <Currency
          allowNegativeValue
          decimalsLimit={10}
          hidePrefix
          suffix="%"
          label={label}
          value={value}
          onValueChange={(value) => setValue(value ?? '')}
        />
      );
    default:
  }
};

const OptionsBuilder: FC<FieldArrayRenderProps> = (arrayHelpers) => {
  const { setFieldValue, handleChange, errors, values } =
    useFormikContext<CREATE_FIELD_FORM>();

  const isInUse = values.isInUse;

  const [selected, setSelected] = useState<CREATE_FIELD_FORM['options']>([]);
  const sortBy = values.sortOptionsBy;

  const ref = useRef<ComponentRef<'input'>>(null);
  const handleDragEnd = (
    result: DropResult,
    arrayHelpers: FieldArrayRenderProps,
  ) => {
    const from = result.source.index;
    const to = result.destination?.index as number;
    arrayHelpers.move(from, to);
  };

  const appendArray = () => {
    arrayHelpers.push({ id: uniqueId('option_'), label: '', value: '' });
  };

  const onSelectAll = () => {
    if (values.options?.length === selected?.length) {
      setSelected([]);
      return;
    } else {
      setSelected(values?.options?.map?.((_val) => _val) ?? []);
    }
  };

  const clearAll = useCallback(() => {
    setFieldValue('options', [
      { id: uniqueId('option_'), label: '', value: '' },
    ]);
    setSelected([]);
  }, [setFieldValue]);

  const onClickDeleteItems = useCallback(() => {
    if (values.options?.length !== selected?.length) {
      setFieldValue(
        'options',
        values.options?.filter(
          (option) => !selected?.some((d) => d.id === option.id),
        ),
      );
      setSelected([]);
    } else {
      clearAll();
    }
  }, [clearAll, selected, setFieldValue, values.options]);

  const headerToShow = useMemo(() => {
    if (!selected?.length) {
      return (
        <>
          <th
            className={cn(
              'text-sm text-title border border-separator-primary font-normal',
            )}
          >
            {' '}
            <div className="flex text-xs items-center px-4 py-3">LABEL</div>
          </th>
          <th
            className={cn(
              'text-sm text-title border border-separator-primary font-normal',
            )}
          >
            {' '}
            <div className="flex text-xs items-center px-4 py-3">
              INTERNAL VALUE
            </div>
          </th>
        </>
      );
    }
    return (
      <>
        <th
          colSpan={2}
          className={cn(
            'text-sm text-content border border-separator-primary font-normal',
          )}
        >
          <button
            type="button"
            className="flex hover:text-danger-base items-center h-[39px] px-2 transition"
            onClick={onClickDeleteItems}
          >
            <div>Delete</div>
          </button>
        </th>
      </>
    );
  }, [onClickDeleteItems, selected?.length]);

  return (
    <>
      <DragDropContext
        onDragEnd={(result) => handleDragEnd(result, arrayHelpers)}
      >
        <Droppable droppableId="options">
          {(provided) => (
            <div className="max-h-[calc(100vh-650px)] overflow-y-auto relative">
              <table
                className="table-auto w-full"
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                <thead>
                  <tr className="bg-[#F1F4F8]">
                    <th
                      className={cn(
                        'text-sm text-title border border-separator-primary font-normal',
                      )}
                    >
                      <div className="flex items-center justify-center h-[39px]">
                        <Checkbox
                          iconType={
                            values.options?.length === selected?.length
                              ? 'check'
                              : 'minus'
                          }
                          checked={!!selected?.length}
                          onChange={onSelectAll}
                        />
                      </div>
                    </th>
                    {headerToShow}
                  </tr>
                </thead>
                <tbody>
                  {values.options?.map((val, index, arr) => (
                    <Draggable
                      isDragDisabled={sortBy === 'alphabetical' || isInUse}
                      key={index}
                      draggableId={`${index}`}
                      index={index}
                    >
                      {(provided) => {
                        return (
                          <tr
                            className="bg-fill-tertiary"
                            ref={provided.innerRef}
                            {...provided.dragHandleProps}
                            {...provided.draggableProps}
                          >
                            <td className="relative">
                              <div className="flex items-center justify-center relative px-5">
                                {sortBy === 'custom' ? (
                                  <DragIcon className="w-3 h-3 absolute left-0 pr-1" />
                                ) : null}
                                <Checkbox
                                  checked={
                                    !!selected?.find((d) => d.id === val.id)
                                  }
                                  onChange={() => {
                                    const option = selected?.find(
                                      (d) => d.id === val.id,
                                    );
                                    // if item is included
                                    if (option) {
                                      setSelected((prev) =>
                                        prev?.filter((d) => d.id !== option.id),
                                      );
                                    } else {
                                      setSelected((prev) => prev?.concat(val));
                                    }
                                  }}
                                />
                              </div>
                            </td>
                            <td className="w-[50%] px-4 py-3">
                              <Input
                                disabled={isInUse}
                                autoComplete="off"
                                {...(index === arr.length - 1 && { ref: ref })}
                                placeholder="Enter label"
                                name={`options.${index}.label`}
                                textType={
                                  (
                                    errors?.options as unknown as {
                                      label: string;
                                      value: string;
                                    }[]
                                  )?.[index]?.label
                                    ? 'danger'
                                    : 'default'
                                }
                                subText={
                                  (
                                    errors?.options as unknown as {
                                      label: string;
                                      value: string;
                                    }[]
                                  )?.[index]?.label ?? ''
                                }
                                onBlur={(e) => {
                                  if (!values.options?.[index].value) {
                                    setFieldValue(
                                      `options.${index}.value`,
                                      e.target.value,
                                    );
                                  }
                                }}
                                onChange={handleChange}
                                value={val.label}
                              />
                            </td>
                            <td className="w-[50%] px-4 py-3">
                              <Input
                                disabled={isInUse}
                                autoComplete="off"
                                placeholder="Enter value"
                                name={`options.${index}.value`}
                                onChange={handleChange}
                                value={val.value}
                                textType={
                                  (
                                    errors?.options as unknown as {
                                      label: string;
                                      value: string;
                                    }[]
                                  )?.[index]?.value
                                    ? 'danger'
                                    : 'default'
                                }
                                subText={
                                  (
                                    errors?.options as unknown as {
                                      label: string;
                                      value: string;
                                    }[]
                                  )?.[index]?.value ?? ''
                                }
                              />
                            </td>
                          </tr>
                        );
                      }}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </tbody>
                <tfoot className="bg-[#F1F4F8] sticky bottom-0">
                  <tr>
                    <td colSpan={3} className="py-[10px] px-3">
                      <div className="flex gap-5">
                        <button
                          type="button"
                          onClick={() => {
                            appendArray();
                            setTimeout(() => ref.current?.focus(), 50);
                          }}
                          className="flex items-center gap-1 text-sm group "
                        >
                          <PlusIcon
                            className={cn(
                              'w-3 h-3 text-title group-hover:text-primary-base transition',
                            )}
                          />
                          <div
                            className={cn(
                              'group-hover:text-primary-base font-semibold transition',
                            )}
                          >
                            ADD AN OPTION
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={clearAll}
                          className="flex items-center gap-1 text-sm group "
                        >
                          <div
                            className={cn(
                              'group-hover:text-primary-base font-semibold transition',
                            )}
                          >
                            CLEAR
                          </div>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </>
  );
};
